/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IMediaModel,
  mediaDetailsTagHelper,
  MediaType,
  TimeHelper,
  useIsPlayableWithDate,
} from "@nf/common";
import { useTranslation } from "next-i18next";
import styles from "./MediaDetailsAdditionalInfo.module.scss";
import cx from "classnames";

export interface MediaDetailsAdditionalInfoProps {
  media: IMediaModel;
}

export const MediaDetailsAdditionalInfo = ({
  media,
}: MediaDetailsAdditionalInfoProps): JSX.Element => {
  const { isPlayable, availableTo } = useIsPlayableWithDate(media);
  const { t } = useTranslation("translations");
  let mediaDescription: JSX.Element | undefined = undefined;
  const mediaAdditionalInformations: string[] = [];

  if (!(media.MediaTypeCode === MediaType.Series)) {
    if (media.Year) {
      mediaAdditionalInformations.push(`${media.Year}`);
    }

    if (media.Duration) {
      mediaAdditionalInformations.push(
        `${TimeHelper.formatDuration(media.Duration)}`
      );
    }

    mediaDescription = (
      <span className={styles.tags}>
        {mediaDetailsTagHelper(media.Categories)
          ?.map((category) => category.CategoryName)
          .join(" | ")}
      </span>
    );
  } else {
    if (!!media.Media?.length) {
      let numberOfEpisodes = 0;
      const numberOfSeasons = media.Media.length;

      media.Media.map(
        (season) =>
          (numberOfEpisodes += !!season.Media?.length
            ? season.Media?.length
            : 0)
      );

      mediaAdditionalInformations.push(
        t("web_series_season", {
          count: numberOfSeasons,
        })
      );

      mediaAdditionalInformations.push(
        t("web_series_episode", { count: numberOfEpisodes })
      );
    }

    mediaDescription = (
      <span className={styles.seriesInfo}>
        {`${t("web_series_serie")} ${media?.Year ? " | " + media.Year : ""}`}
      </span>
    );
  }

  const mediaAdditionalInfo = !!mediaAdditionalInformations.length && (
    <p>{mediaAdditionalInformations.join(" | ")}</p>
  );

  const mediaExpiration = isPlayable && availableTo && (
    <span className={styles.expire}>
      {t("web_asset_page_expire")} {availableTo}
    </span>
  );

  return (
    <div className={cx(styles.mediaAdditionalInfo)}>
      <h1>{media.Title}</h1>
      {mediaDescription}
      {mediaAdditionalInfo}
      {mediaExpiration}
    </div>
  );
};

export default MediaDetailsAdditionalInfo;
